import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [teacher, setTeacher] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [comments, setComments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [res1, res2, res3, res4] = await Promise.all([
          fetch(`${API_URL}/api/teacher/${id}`),
          fetch(`${API_URL}/api/teacher/materials/teacher/${id}`),
          fetch(`${API_URL}/api/teacher/comments/${id}`),
          fetch(`${API_URL}/api/teacher/class/teacher/${id}`),
        ]);

        if (!res1.ok) throw new Error("Teacher not found");

        const data1 = await res1.json();
        const data2 = await res2.json();
        const data3 = await res3.json();
        const data4 = await res4.json();

        setTeacher(data1);
        setMaterials(data2);

        const sortedComments = data3.slice().sort((a, b) =>
          new Date(b.ratedAt || b.createdAt) - new Date(a.ratedAt || a.createdAt)
        );
        setComments(sortedComments);

        const sortedClasses = data4.slice().sort((a, b) =>
          new Date(a.scheduledAt) - new Date(b.scheduledAt)
        );
        setClasses(sortedClasses);

      } catch (err) {
        console.error("Error:", err);
        setError("❌ Failed to load teacher data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDownload = (filePath) => {
    const isLoggedIn = localStorage.getItem("user");
    if (!isLoggedIn) {
      alert("Please log in to download this file.");
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      navigate("/login");
      return;
    }
    window.open(`${API_URL}/${filePath}`, "_blank");
  };

  const handleJoinClass = (cls) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      navigate("/login");
      return;
    }
    alert(`Joining class: ${cls.title}`);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <svg
          className="w-16 h-16 text-blue-600 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          ></path>
        </svg>
        <p className="text-blue-600 text-xl mt-4">Loading teacher profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-10 text-red-600 font-semibold">
        {error}
        <br />
        <button
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => navigate(-1)}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-10 sm:p-8">
      <button
        onClick={() => navigate(-1)}
        className="text-blue-600 hover:underline mb-6"
      >
        ← Back
      </button>

      <section className="mb-8">
        <h1 className="text-3xl font-bold mb-1">{teacher.name}</h1>
        <p className="text-lg text-gray-700 mb-1">{teacher.subject}</p>
        <p className="text-md mb-1">
          📧 <span className="font-medium">{teacher.email}</span>
        </p>
        <p className="text-md">
          ⭐ Rating: <span className="font-semibold">{teacher.rating?.toFixed(1) || "N/A"}</span>
        </p>
      </section>

      {/* Classes */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">📅 Scheduled Classes</h2>
        {classes.length === 0 ? (
          <p className="text-gray-500">No classes scheduled by this teacher.</p>
        ) : (
          <ul className="space-y-4">
            {classes.map((cls) => (
              <li
                key={cls._id}
                className="border p-4 rounded-lg shadow-sm flex flex-col sm:flex-row sm:justify-between sm:items-center"
              >
                <div>
                  <h3 className="text-lg font-bold">{cls.title}</h3>
                  <p className="text-sm text-gray-700">🕒 {formatDate(cls.scheduledAt)}</p>
                  <p className="text-sm text-gray-600">📍 {cls.location || "Online"}</p>
                </div>
                <button
                  onClick={() => handleJoinClass(cls)}
                  className="mt-3 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Join
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Materials */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">📚 Uploaded Materials</h2>
        {materials.length === 0 ? (
          <p className="text-gray-500">No materials uploaded by this teacher.</p>
        ) : (
          <ul className="space-y-4">
            {materials.map((mat) => (
              <li
                key={mat._id}
                className="border p-4 rounded-lg shadow-sm flex flex-col sm:flex-row sm:justify-between sm:items-center"
              >
                <div>
                  <h3 className="text-lg font-bold">{mat.title}</h3>
                  <p className="text-sm text-gray-600 mb-1">{mat.description}</p>
                </div>
                <button
                  onClick={() => handleDownload(mat.path)}
                  className="text-blue-600 hover:underline mt-2 sm:mt-0"
                >
                  📥 Download {mat.originalName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Comments */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">💬 Student Ratings & Comments</h2>
        {comments.length === 0 ? (
          <p className="text-gray-500">No feedback yet from students.</p>
        ) : (
          <ul className="space-y-6">
            {comments.map((com, idx) => (
              <li
                key={idx}
                className="border p-4 rounded-lg bg-gray-50 shadow-sm"
              >
                <div className="flex justify-between items-center mb-1">
                  <p className="text-md font-semibold">⭐ {com.rating}/5</p>
                  <p className="text-xs text-gray-400 italic">
                    {formatDate(com.ratedAt || com.createdAt)}
                  </p>
                </div>
                <p className="text-gray-700 italic mb-2">"{com.comment}"</p>
                <p className="text-sm text-gray-500 text-right">– {com.studentName || "Anonymous"}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
